import 'maplibre-gl/dist/maplibre-gl.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { AttributionControl, Map as MapLibreMap, NavigationControl } from 'maplibre-gl';
import {
  Crosshair,
  Eraser,
  MapPinned,
  MousePointer2,
  Pencil,
  Pentagon,
  Search,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  ApiRequestError,
  adminAreaAt,
  adoptionPreview,
  checkSiteArea,
  matchAreaBorder,
  fetchPlacementReview,
  fetchSiteAreas,
  normalizeArea,
  saveSiteArea,
  searchAdminAreas,
} from '../../api/client';
import { usePrefs } from '../../app/prefs';
import { ErrorNote } from '../admin/ErrorNote';
import { baseStyle, cssVar } from '../map/baseStyle';
import './areaSuggest.css';
import { bounds, fromGeometry, toGeometry, type Poly, type Pos } from './areaGeometry';
import { DrawTool, type DrawMode } from './drawTool';
import { decidesPlacement, PlacementReview, placementKey } from './PlacementReview';
import { SITES_KEY, useAdminSites } from './sitesData';
import { useMe } from '../auth/session';

export const AREAS_KEY = ['admin', 'site-areas'] as const;

/** Surface for people: m² below a hectare, hectares below a km², km² above. */
export function areaText(m2: number, locale: string): string {
  const f = (v: number, d: number) => v.toLocaleString(locale, { maximumFractionDigits: d });
  return m2 < 10_000
    ? `${f(m2, 0)} m²`
    : m2 < 1_000_000
      ? `${f(m2 / 10_000, 2)} ha`
      : `${f(m2 / 1_000_000, 2)} km²`;
}

/**
 * Full-map editor for the area of one site (U12). Other areas are shown for context: the site's own client tree
 * as outlines, other clients as greyed "taken" areas. While drawing, the area is checked against the areas of the
 * site's siblings (other clients for a client, the other sites under the same parent otherwise): every overlapping
 * part is drawn in red and listed, and saving stays disabled until none is left (the server refuses it as well).
 */
export function AreaEditor() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = usePrefs((s) => s.theme);
  const sites = useAdminSites();
  const me = useMe();
  const areas = useQuery({ queryKey: AREAS_KEY, queryFn: fetchSiteAreas });
  const site = sites.data?.items.find((s) => s.id === id);
  const canProbe = Boolean(sites.data?.can_create_client);
  const ref = useRef<HTMLDivElement>(null);
  const toolRef = useRef<DrawTool | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [mode, setMode] = useState<DrawMode>('edit');
  const [state, setState] = useState<{ parts: Poly[]; selected: number | null; canUndo: boolean }>({
    parts: [],
    selected: null,
    canUndo: false,
  });
  const [dirty, setDirty] = useState(false);
  const [probe, setProbe] = useState<{ at: [number, number]; result: api.AdoptionPreview | null } | null>(
    null,
  );
  const [conflict, setConflict] = useState<string | null>(null);
  const [overlaps, setOverlaps] = useState<api.SiteAreaOverlap[]>([]);
  const [checking, setChecking] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Boundary suggestions (OpenStreetMap commune limits): found by name or picked on the map, previewed, then applied.
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ enabled: boolean; items: api.AdminArea[] } | null>(null);
  const [proposal, setProposal] = useState<api.AdminArea | null>(null);
  const search = useMutation({
    mutationFn: (q: string) => searchAdminAreas(q),
    onSuccess: (r) => {
      setFound(r);
      setProposal(r.items[0] ?? null);
    },
  });
  const pick = useMutation({
    mutationFn: (at: Pos) => adminAreaAt(at[0], at[1]),
    onSuccess: (r) => {
      setFound({ enabled: r.enabled, items: r.area ? [r.area] : [] });
      setProposal(r.area);
    },
  });
  const pickRef = useRef<(at: Pos) => void>(() => undefined);
  useEffect(() => {
    pickRef.current = pick.mutate;
  });

  const own = useMemo(() => areas.data?.features.find((f) => f.id === id), [areas.data, id]);
  const ready = Boolean(site && areas.data);

  useEffect(() => {
    if (!ref.current || !ready || !site || !areas.data) return;
    const map = new MapLibreMap({
      container: ref.current,
      style: baseStyle(theme),
      center: site.lon !== null && site.lat !== null ? [site.lon, site.lat] : [26.1, 45.9],
      zoom: site.lon !== null ? 13 : 6,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new AttributionControl({ compact: false }), 'bottom-right');
    const initial = fromGeometry(own?.geometry);
    const context = areas.data.features.filter((f) => f.id !== id);
    const b =
      bounds(initial) ??
      bounds(context.filter((f) => f.id === site.parent_id).flatMap((f) => fromGeometry(f.geometry))) ??
      null;
    if (b) map.fitBounds(b, { padding: 80, duration: 0, maxZoom: 16 });

    map.once('style.load', () => {
      const clientId =
        own?.properties.client_id ?? context.find((f) => f.id === site.parent_id)?.properties.client_id;
      map.addSource('ctx', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: context.map((f) => ({
            ...f,
            properties: {
              ...f.properties,
              same: f.properties.client_id === (clientId ?? site.id),
              taken: f.properties.kind === 'organization' && f.properties.client_id !== (clientId ?? site.id),
            },
          })),
        },
        promoteId: 'id',
      });
      map.addLayer({
        id: 'ctx-fill',
        type: 'fill',
        source: 'ctx',
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'conflict'], false],
            cssVar('--color-critical'),
            '#8a8f98',
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'conflict'], false],
            0.12,
            ['get', 'taken'],
            0.25,
            0,
          ],
        },
      });
      map.addLayer({
        id: 'ctx-line',
        type: 'line',
        source: 'ctx',
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'conflict'], false],
            cssVar('--color-critical'),
            ['get', 'same'],
            cssVar('--color-accent'),
            '#8a8f98',
          ],
          'line-width': ['case', ['boolean', ['feature-state', 'conflict'], false], 2.5, 1.5],
          'line-dasharray': [3, 2],
        },
      });
      map.addLayer({
        id: 'ctx-label',
        type: 'symbol',
        source: 'ctx',
        layout: { 'text-field': ['get', 'name'], 'text-size': 12 },
        paint: {
          'text-color': cssVar('--color-text-muted'),
          'text-halo-color': cssVar('--color-surface'),
          'text-halo-width': 1.5,
        },
      });
      // Overlapping parts of the drawn area (refreshed by the live check), above the context, under the drawing.
      map.addSource('overlap', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'overlap-fill',
        type: 'fill',
        source: 'overlap',
        paint: { 'fill-color': cssVar('--color-critical') || '#d92d20', 'fill-opacity': 0.45 },
      });
      map.addLayer({
        id: 'overlap-line',
        type: 'line',
        source: 'overlap',
        paint: { 'line-color': cssVar('--color-critical') || '#d92d20', 'line-width': 2 },
      });
      map.addSource('proposal', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'proposal-fill',
        type: 'fill',
        source: 'proposal',
        paint: { 'fill-color': cssVar('--brand-warm') || '#f58021', 'fill-opacity': 0.12 },
      });
      map.addLayer({
        id: 'proposal-line',
        type: 'line',
        source: 'proposal',
        paint: {
          'line-color': cssVar('--brand-warm') || '#f58021',
          'line-width': 2.5,
          'line-dasharray': [2, 1.5],
        },
      });
      const tool = new DrawTool(
        map,
        {
          fill: cssVar('--brand-cool') || '#1c63b7',
          line: cssVar('--brand-cool') || '#1c63b7',
          vertex: cssVar('--brand-warm') || '#f58021',
          selected: cssVar('--brand-warm') || '#f58021',
        },
        (s) => {
          setState(s);
          setDirty(true);
        },
        (at) => {
          setProbe({ at, result: null });
          void adoptionPreview(at[0], at[1]).then(
            (result) => setProbe({ at, result }),
            () => setProbe(null),
          );
        },
        async (parts) => {
          const g = toGeometry(parts);
          return g ? fromGeometry((await normalizeArea(g)).geometry) : [];
        },
        (at) => pickRef.current(at),
      );
      tool.setParts(initial);
      setDirty(false);
      toolRef.current = tool;
    });
    return () => {
      toolRef.current?.destroy();
      toolRef.current = null;
      mapRef.current = null;
      map.remove();
    };
    // The map is built once per site/theme; edits never rebuild it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, id, theme]);

  useEffect(() => toolRef.current?.setMode(mode), [mode]);

  // Show the proposed boundary (not part of the area until applied) and bring it into view.
  useEffect(() => {
    const map = mapRef.current;
    const src = map?.getSource('proposal') as { setData(d: unknown): void } | undefined;
    if (!map || !src) return;
    src.setData({
      type: 'FeatureCollection',
      features: proposal ? [{ type: 'Feature', properties: {}, geometry: proposal.geometry }] : [],
    });
    if (proposal) {
      const [w, so, e, n] = proposal.bbox;
      map.fitBounds(
        [
          [w, so],
          [e, n],
        ],
        { padding: 80, maxZoom: 16 },
      );
    }
  }, [proposal]);

  /** Replaces the drawn area by the proposal; the previous area stays in the undo history. */
  const applyProposal = () => {
    if (!proposal) return;
    toolRef.current?.setParts(fromGeometry(proposal.geometry), true);
    setProposal(null);
    setMode('edit');
  };
  const describe = (a: api.AdminArea) =>
    [
      a.kind
        ? t(`map.proposals.kind.${a.kind}` as 'map.proposals.kind.municipality', { defaultValue: a.kind })
        : null,
      a.county,
    ]
      .filter(Boolean)
      .join(' · ');

  const back = () => navigate('/sites', { state: { open: id } });
  const save = useMutation({
    mutationFn: () => saveSiteArea(id, toGeometry(state.parts)),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: AREAS_KEY }),
        qc.invalidateQueries({ queryKey: SITES_KEY }),
      ]);
      setDirty(false);
      // Devices whose position now lies in another area: offer to move them before leaving (user, 2026-10-08).
      const review = await fetchPlacementReview(id).catch(() => null);
      if (review?.items.length) {
        qc.setQueryData(placementKey(id), review);
        setReviewOpen(true);
      } else back();
    },
    onError: (e) => {
      const other =
        e instanceof ApiRequestError && e.code === 'AREA_OVERLAP' ? String(e.params?.client_id ?? '') : null;
      setConflict(other);
    },
  });

  // Live overlap check of the drawn area (debounced; a newer drawing discards older answers).
  const checkSeq = useRef(0);
  useEffect(() => {
    const geometry = toGeometry(state.parts);
    const seq = ++checkSeq.current;
    if (!geometry) {
      setOverlaps([]);
      setChecking(false);
      return;
    }
    setChecking(true);
    const timer = setTimeout(() => {
      checkSiteArea(id, geometry).then(
        (r) => {
          if (seq !== checkSeq.current) return;
          setOverlaps(r.overlaps);
          setChecking(false);
          setConflict(null);
        },
        () => seq === checkSeq.current && setChecking(false),
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [state.parts, id]);

  // Overlapping parts in red; the sites they overlap (and a conflict reported on save) outlined in red.
  useEffect(() => {
    const map = mapRef.current;
    const src = map?.getSource('overlap') as { setData(d: unknown): void } | undefined;
    if (!map || !src) return;
    src.setData({
      type: 'FeatureCollection',
      features: overlaps.map((o) => ({
        type: 'Feature',
        properties: { name: o.name },
        geometry: o.geometry,
      })),
    });
    const hit = new Set([...overlaps.map((o) => o.site_id), ...(conflict ? [conflict] : [])]);
    for (const f of areas.data?.features ?? [])
      map.setFeatureState({ source: 'ctx', id: f.id }, { conflict: hit.has(f.id) });
  }, [overlaps, conflict, areas.data]);

  // "Match border": this site's area retreats to the neighbours' border (one undoable step).
  const match = useMutation({
    mutationFn: (siteIds: string[]) => {
      const g = toGeometry(state.parts);
      if (!g) return Promise.resolve({ geometry: null });
      return matchAreaBorder(id, g, siteIds);
    },
    onSuccess: (r) => {
      if (r.geometry) toolRef.current?.setParts(fromGeometry(r.geometry), true);
      else setMatchEmpty(true);
    },
  });
  const [matchEmpty, setMatchEmpty] = useState(false);
  useEffect(() => setMatchEmpty(false), [overlaps]);

  const showOverlap = (o: api.SiteAreaOverlap) => {
    const b = bounds(fromGeometry(o.geometry));
    if (b) mapRef.current?.fitBounds(b, { padding: 120, maxZoom: 17 });
  };

  const TOOLS: { mode: DrawMode; icon: typeof Pencil; label: string }[] = [
    { mode: 'edit', icon: MousePointer2, label: t('area.tools.edit') },
    { mode: 'freehand', icon: Pencil, label: t('area.tools.freehand') },
    { mode: 'polygon', icon: Pentagon, label: t('area.tools.polygon') },
    ...(canProbe ? [{ mode: 'probe' as const, icon: Crosshair, label: t('area.tools.probe') }] : []),
    { mode: 'pick', icon: MapPinned, label: t('area.tools.pick') },
  ];

  if (sites.isSuccess && !site) return <div className="page muted">{t('errors.SITE_NOT_FOUND')}</div>;
  return (
    <div className="map-wrap area-editor">
      <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
      <div className="area-panel card">
        <div
          className="card-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
        >
          <div>
            <strong>{t('area.title', { name: site?.name ?? '…' })}</strong>
            <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {t(`area.hint.${mode}`)}
            </div>
          </div>
          <div className="area-tools" role="toolbar" aria-label={t('area.toolbar')}>
            {TOOLS.map((tool) => (
              <button
                key={tool.mode}
                type="button"
                className="icon-btn"
                aria-pressed={mode === tool.mode}
                title={tool.label}
                aria-label={tool.label}
                onClick={() => setMode(tool.mode)}
              >
                <tool.icon size={18} />
              </button>
            ))}
            <span className="area-sep" />
            <button
              type="button"
              className="icon-btn"
              title={t('area.undo')}
              aria-label={t('area.undo')}
              disabled={!state.canUndo}
              onClick={() => toolRef.current?.undo()}
            >
              <Undo2 size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              title={t('area.deletePart')}
              aria-label={t('area.deletePart')}
              disabled={state.selected === null}
              onClick={() => toolRef.current?.deleteSelected()}
            >
              <Trash2 size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              title={t('area.clear')}
              aria-label={t('area.clear')}
              disabled={!state.parts.length}
              onClick={() => toolRef.current?.clear()}
            >
              <Eraser size={18} />
            </button>
          </div>
          <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {t('area.parts', { count: state.parts.length })}
          </span>
          <form
            className="area-suggest"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim().length >= 2) search.mutate(query.trim());
            }}
          >
            <span className="small muted">{t('area.suggest.title')}</span>
            <div className="area-suggest-row">
              <input
                className="input"
                value={query}
                placeholder={t('area.suggest.placeholder')}
                aria-label={t('area.suggest.placeholder')}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button type="submit" className="btn" disabled={search.isPending || query.trim().length < 2}>
                <Search size={16} />{' '}
                {search.isPending ? t('area.suggest.searching') : t('area.suggest.search')}
              </button>
            </div>
            {pick.isPending && <span className="small muted">{t('area.suggest.searching')}</span>}
            {found && !found.enabled && <div className="notice">{t('area.suggest.disabled')}</div>}
            {found?.enabled && !found.items.length && (
              <span className="small muted">{t('area.suggest.none')}</span>
            )}
            {found && found.items.length > 1 && (
              <ul className="area-suggest-list">
                {found.items.map((a) => (
                  <li key={a.osm_ref}>
                    <button
                      type="button"
                      aria-pressed={proposal?.osm_ref === a.osm_ref}
                      onClick={() => setProposal(a)}
                    >
                      <strong>{a.name}</strong> <span className="small muted">{describe(a)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {proposal && (
              <div className="notice area-suggest-pick">
                <div>
                  <strong>{proposal.name}</strong> <span className="small muted">{describe(proposal)}</span>
                </div>
                <span className="small muted">{t('area.suggest.useHint')}</span>
                <div className="toolbar">
                  <button type="button" className="btn btn-primary" onClick={applyProposal}>
                    {t('area.suggest.use')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setProposal(null)}>
                    {t('area.suggest.dismiss')}
                  </button>
                </div>
              </div>
            )}
            <ErrorNote error={search.error ?? pick.error} />
            {found?.enabled && <span className="small muted">{t('area.suggest.source')}</span>}
          </form>
          {probe && (
            <div className="notice" role="status">
              {probe.result === null
                ? t('common.loading')
                : probe.result.site_id
                  ? t('area.probeHit', { path: probe.result.path.join(' › ') })
                  : t('area.probeMiss')}
            </div>
          )}
          {overlaps.length > 0 && (
            <div className="form-error area-overlaps" role="alert">
              <strong>{t('area.overlap.title', { count: overlaps.length })}</strong>
              <ul>
                {overlaps.map((o) => (
                  <li key={o.site_id}>
                    <button type="button" className="link-btn" onClick={() => showOverlap(o)}>
                      {t('area.overlap.item', { name: o.name, size: areaText(o.area_m2, i18n.language) })}
                    </button>
                    <button
                      type="button"
                      className="btn area-match-btn"
                      disabled={match.isPending}
                      title={t('area.overlap.matchHint', { name: o.name })}
                      onClick={() => match.mutate([o.site_id])}
                    >
                      {t('area.overlap.match')}
                    </button>
                  </li>
                ))}
              </ul>
              {overlaps.length > 1 && (
                <button
                  type="button"
                  className="btn area-match-btn"
                  disabled={match.isPending}
                  onClick={() => match.mutate(overlaps.map((o) => o.site_id))}
                >
                  {t('area.overlap.matchAll')}
                </button>
              )}
              {matchEmpty && <div>{t('area.overlap.matchEmpty')}</div>}
              <ErrorNote error={match.error} />
              <span className="small">{t('area.overlap.fix')}</span>
            </div>
          )}
          {checking && overlaps.length === 0 && (
            <span className="small muted" role="status">
              {t('area.overlap.checking')}
            </span>
          )}
          <ErrorNote error={save.error} />
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty || save.isPending || overlaps.length > 0}
              title={overlaps.length > 0 ? t('area.overlap.blocked') : undefined}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={back}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
      <div className="map-legend area-legend">
        <div className="card">
          <div className="card-body">
            <span className="toolbar">
              <span className="legend-swatch" style={{ background: 'var(--brand-cool)' }} />{' '}
              {t('area.legend.this')}
            </span>
            <span className="toolbar">
              <span className="legend-swatch legend-dashed" style={{ borderColor: 'var(--color-accent)' }} />{' '}
              {t('area.legend.sameClient')}
            </span>
            <span className="toolbar">
              <span className="legend-swatch" style={{ background: '#8a8f98' }} /> {t('area.legend.taken')}
            </span>
            {overlaps.length > 0 && (
              <span className="toolbar">
                <span className="legend-swatch" style={{ background: 'var(--color-critical)' }} />{' '}
                {t('area.legend.overlap')}
              </span>
            )}
            {proposal && (
              <span className="toolbar">
                <span className="legend-swatch legend-dashed" style={{ borderColor: 'var(--brand-warm)' }} />{' '}
                {t('area.legend.proposal')}
              </span>
            )}
          </div>
        </div>
      </div>
      {reviewOpen && (
        <PlacementReview
          siteId={id}
          sites={sites.data?.items ?? []}
          canDecide={decidesPlacement(me.data)}
          onClose={back}
        />
      )}
    </div>
  );
}
